import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { RiskScoreBadge, StatusBadge, PatternIndicator } from '@/components/ui/Badge/index'
import { DateDisplay } from '@/components/ui/Utility'
import { CheckCircle, XCircle, Clock, MoreHorizontal } from 'lucide-react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { toast } from '@/hooks/use-toast'
import type { Lead } from '@/services/mockData'

interface RecentLeadsTableProps {
  leads: Lead[]
}

export const RecentLeadsTable: React.FC<RecentLeadsTableProps> = ({ leads }) => {
  const [selectedLeads, setSelectedLeads] = useState<string[]>([])
  const [leadStatuses, setLeadStatuses] = useState<Record<string, 'approved' | 'rejected' | 'pending'>>({})

  const handleLeadAction = (leadId: string, action: 'approve' | 'reject') => {
    setLeadStatuses(prev => ({ ...prev, [leadId]: action === 'approve' ? 'approved' : 'rejected' }))
    toast({
      title: `Lead ${action}d`,
      description: `Lead ${leadId.slice(0, 8)} has been ${action}d successfully.`,
      variant: action === 'approve' ? 'default' : 'destructive'
    })
  }

  const getPatternFlags = (flags: string[]) => {
    const patterns = {
      sequentialPhone: flags.includes('sequential_phone'),
      datacenterIP: flags.includes('datacenter_ip'),
      suspiciousEmail: flags.includes('suspicious_email'),
      velocityAttack: flags.includes('velocity_attack'),
      zipConcentration: flags.includes('zip_concentration')
    }
    return patterns
  }

  const getLeadStatus = (lead: Lead) => {
    if (leadStatuses[lead.id]) return leadStatuses[lead.id]
    return lead.status
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            Recent Leads
            <span className="text-sm font-normal text-muted-foreground">
              ({leads.length} shown)
            </span>
          </CardTitle>
          {selectedLeads.length > 0 && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline">
                Approve ({selectedLeads.length})
              </Button>
              <Button size="sm" variant="destructive">
                Reject ({selectedLeads.length})
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="min-w-[800px] w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="w-[120px] px-4 py-3 text-left text-sm font-medium text-muted-foreground">Time</th>
                <th className="w-[200px] px-4 py-3 text-left text-sm font-medium text-muted-foreground">Contact</th>
                <th className="w-[150px] px-4 py-3 text-left text-sm font-medium text-muted-foreground">Location</th>
                <th className="w-[100px] px-4 py-3 text-left text-sm font-medium text-muted-foreground">Risk Score</th>
                <th className="w-[150px] px-4 py-3 text-left text-sm font-medium text-muted-foreground">Patterns</th>
                <th className="w-[100px] px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                <th className="w-[100px] px-4 py-3 text-left text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {leads.slice(0, 10).map((lead) => (
                <tr key={lead.id} className="hover:bg-muted/50 transition-colors">
                  <td className="px-4 py-4 text-sm">
                    <DateDisplay date={lead.timestamp} format="relative" showTime />
                  </td>
                  
                  <td className="px-4 py-4 text-sm">
                    <div className="space-y-1">
                      <p className="font-medium text-foreground truncate" title={lead.name}>
                        {lead.name}
                      </p>
                      <p className="text-muted-foreground truncate text-xs" title={lead.email}>
                        {lead.email}
                      </p>
                      <p className="text-muted-foreground text-xs">{lead.phone}</p>
                    </div>
                  </td>
                  
                  <td className="px-4 py-4 text-sm">
                    <div className="space-y-1">
                      <p className="text-foreground">{lead.zip}</p>
                      <p className="text-muted-foreground text-xs truncate" title={lead.ip}>
                        {lead.ip}
                      </p>
                    </div>
                  </td>
                  
                  <td className="px-4 py-4 text-sm">
                    <RiskScoreBadge score={lead.riskScore} size="sm" />
                  </td>
                  
                  <td className="px-4 py-4 text-sm">
                    <PatternIndicator 
                      patterns={getPatternFlags(lead.flags)} 
                      layout="compact" 
                      showLabels={false}
                    />
                  </td>
                  
                  <td className="px-4 py-4 text-sm">
                    <StatusBadge 
                      status={getLeadStatus(lead)} 
                      size="sm" 
                    />
                  </td>
                  
                  <td className="px-4 py-4 text-sm">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={() => handleLeadAction(lead.id, 'approve')}
                          disabled={getLeadStatus(lead) !== 'pending'}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleLeadAction(lead.id, 'reject')}
                          disabled={getLeadStatus(lead) !== 'pending'}
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {leads.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No recent leads to display</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}